import {
  CONSENT_COOKIE_NAME,
  CONSENT_MAX_AGE_SEC,
  CONSENT_STORAGE_KEY,
  choiceToRecord,
  encodeConsentCookie,
  isConsentRecordCurrent,
  parseConsentCookie,
  type ConsentChoice,
  type CookieConsentRecord,
} from "@shared/cookie-consent";
import { readBrowserCookie } from "@/lib/csrf-cookie";
import { setBrowserCookie } from "@/lib/browser-cookie";

export const COOKIE_CONSENT_OPEN_EVENT = "autoplus:open-cookie-consent";

export function openCookieConsentBanner(): void {
  if (typeof window === "undefined") return;
  window.dispatchEvent(new Event(COOKIE_CONSENT_OPEN_EVENT));
}

function readStoredConsent(): CookieConsentRecord | null {
  if (typeof window === "undefined") return null;
  try {
    const raw = localStorage.getItem(CONSENT_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as CookieConsentRecord;
    return isConsentRecordCurrent(parsed) ? parsed : null;
  } catch {
    return null;
  }
}

function readConsentFromCookie(): ConsentChoice | null {
  return parseConsentCookie(readBrowserCookie(CONSENT_COOKIE_NAME));
}

export function hasConsentChoice(): boolean {
  return readStoredConsent() !== null || readConsentFromCookie() !== null;
}

export function getConsentRecord(): CookieConsentRecord | null {
  const stored = readStoredConsent();
  if (stored) return stored;

  const fromCookie = readConsentFromCookie();
  if (!fromCookie) return null;
  return choiceToRecord(fromCookie);
}

export function hasFunctionalConsent(): boolean {
  const record = getConsentRecord();
  return record?.functional === true;
}

export function hasAnalyticsConsent(): boolean {
  const record = getConsentRecord();
  return record?.analytics === true;
}

function persistLocal(record: CookieConsentRecord): void {
  localStorage.setItem(CONSENT_STORAGE_KEY, JSON.stringify(record));
}

function persistClientCookie(choice: ConsentChoice): void {
  setBrowserCookie(CONSENT_COOKIE_NAME, encodeConsentCookie(choice), CONSENT_MAX_AGE_SEC);
}

async function persistServerCookie(choice: ConsentChoice): Promise<void> {
  const response = await fetch("/api/cookie-consent", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    credentials: "include",
    body: JSON.stringify({ choice }),
  });
  if (!response.ok) {
    throw new Error("cookie-consent api failed");
  }
}

export async function saveConsentChoice(choice: ConsentChoice): Promise<void> {
  const record = choiceToRecord(choice);
  persistLocal(record);
  persistClientCookie(choice);

  try {
    await persistServerCookie(choice);
  } catch {
    // localStorage + client cookie already saved; server sync is best-effort
  }
}
